import { useState } from "react";
import type { PaygapMetricItemDto } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import { AnalysisParameters } from "./usePaygapParameters";

import { useRunAnalysis } from "@api/hooks/paygap";
import { useUserStore } from "@store/user";

export const usePaygapAnalysis = (): {
  // State
  analysisError: string | null;
  isFetchingMetrics: boolean;
  metricsError: string | null;
  showAnalysisConfirmation: boolean;
  showRequestSent: boolean;
  dataFetchedFromDatabase: boolean;

  // Actions
  handleStartAnalysis: (
    parameters: AnalysisParameters,
    hasValidData: boolean,
    onComplete?: (results: PaygapMetricItemDto[]) => void,
  ) => Promise<void>;
  handleFetchMetrics: (
    onAnalysisComplete?: (results: PaygapMetricItemDto[]) => void,
  ) => Promise<void>;
  testNetworkConnectivity: () => Promise<boolean>;
  setShowAnalysisConfirmation: (open: boolean) => void;
  setDataFetchedFromDatabase: (fetched: boolean) => void;
  setAnalysisError: (message: string | null) => void;
  setMetricsError: (message: string | null) => void;
} => {
  const { user } = useUserStore();
  const { runAnalysis } = useRunAnalysis(user);

  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [isFetchingMetrics, setIsFetchingMetrics] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [showAnalysisConfirmation, setShowAnalysisConfirmation] = useState(false);
  const [showRequestSent, setShowRequestSent] = useState(false);
  const [dataFetchedFromDatabase, setDataFetchedFromDatabase] = useState(false);

  const { t } = useTranslation();

  // Test network connectivity to the required endpoints
  const testNetworkConnectivity = async (): Promise<boolean> => {
    const endpoints = [import.meta.env.VITE_API_BASE_URL];

    for (const endpoint of endpoints) {
      try {
        await fetch(`${endpoint}/health`, {
          method: "HEAD",
          mode: "no-cors",
        });

        return true;
      } catch {
        // Endpoint not reachable, try next
      }
    }

    return false;
  };

  const handleStartAnalysis = async (
    parameters: AnalysisParameters,
    hasValidData: boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _onAnalysisComplete?: (results: PaygapMetricItemDto[]) => void,
  ): Promise<void> => {
    setAnalysisError(null);

    // If no CSV data, check if there's data in the database
    if (!hasValidData) {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/api/paygap/personnel`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          const data = await response.json();

          if (!data || (Array.isArray(data) && data.length === 0)) {
            throw new Error(
              "No data found in database. Please upload a CSV file or add data through Manage Employee Data first.",
            );
          }
        } else {
          throw new Error("Failed to check database for existing data.");
        }
      } catch (error) {
        setAnalysisError(
          error instanceof Error
            ? error.message
            : "Failed to check database for existing data.",
        );

        return;
      }
    }

    // Test network connectivity first
    await testNetworkConnectivity();

    try {
      const educationAndTraining = (parameters.educationAndTraining || [])
        .filter((item) => item.id || item.numberOfYears || item.name)
        .slice(0, 8)
        .map((item) => ({
          id: Number(item.id) || 0,
          numberOfYears: Number(item.numberOfYears) || 0,
          name: (item.name || "").trim(),
        }));

      const professionalPositions = (parameters.professionalPositions || [])
        .filter((item) => item.id || item.name)
        .slice(0, 5)
        .map((item) => ({
          id: Number(item.id) || 0,
          name: (item.name || "").trim(),
        }));

      // Note: Currency is now saved to database via usePaygapParameters hook
      // and read from API settings in analysis components
      const standardWorkweekValue = parseFloat(
        String(parameters.standardWorkweek || "").replace(",", "."),
      );

      const analysisConfig = {
        currency: parameters.currency.toLowerCase(),
        ageFormat: parameters.ageFormat,
        yearOfServiceFormat: parameters.serviceFormat,
        standardWorkweek: Number.isNaN(standardWorkweekValue)
          ? 0
          : standardWorkweekValue,
        referenceDate: parameters.referenceYear,
        regression: Number(parameters.regression || "1") || 1,
        educationAndTraining,
        professionalPositions,
      };

      // Send request to backend proxy (which forwards to Azure Logic App)
      const response = await runAnalysis({ analysisConfig });

      if (response.success) {
        // Show success message
        setShowRequestSent(true);

        // Hide the success message after 10 seconds
        setTimeout(() => {
          setShowRequestSent(false);
        }, 10000);
      } else {
        throw new Error(response.message || "Analysis request failed");
      }
    } catch (error) {
      let errorMessage = "Failed to start analysis.";

      if (error instanceof Error) {
        if (error.message.includes("Failed to fetch")) {
          errorMessage =
            "Network error: Unable to connect to the server. Please check your internet connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }

      setAnalysisError(errorMessage);
    }
  };

  const handleFetchMetrics = async (
    onAnalysisComplete?: (results: PaygapMetricItemDto[]) => void,
  ): Promise<void> => {
    setIsFetchingMetrics(true);
    setMetricsError(null);

    try {
      const metricsResponse = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/paygap/metrics`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
            "Content-Type": "application/json",
          },
        },
      );

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/paygap/personnel`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const data = await response.json();

        if (!data || (Array.isArray(data) && data.length === 0)) {
          throw new Error(t("paygap.analysis.noEmployees"));
        }
      } else {
        throw new Error("Failed to check database for existing data.");
      }

      if (!metricsResponse.ok) {
        if (metricsResponse.status === 404) {
          throw new Error(t("paygap.analysis.metricsNoData"));
        }
        const errorText = await metricsResponse.text();

        throw new Error(
          `Failed to fetch metrics: HTTP ${metricsResponse.status}, body: ${errorText}`,
        );
      }

      const metricsData = await metricsResponse.json();

      if (!metricsData || (Array.isArray(metricsData) && metricsData.length === 0)) {
        throw new Error(t("paygap.analysis.metricsNoData"));
      }

      // Backend returns PaygapMetricItemDto[] directly
      let metrics: PaygapMetricItemDto[];

      if (Array.isArray(metricsData) && metricsData.length > 0) {
        // Normalize to ensure correct shape
        metrics = metricsData.map(
          (
            item: { item?: string; key?: string; value?: string; val?: string },
            index: number,
          ) => ({
            item: item.item || item.key || `item_${index}`,
            value: String(item.value || item.val || ""),
          }),
        );
      } else if (typeof metricsData === "object" && metricsData !== null) {
        // Convert object to array format
        metrics = Object.entries(metricsData).map(([key, value]) => ({
          item: key,
          value: String(value),
        }));
      } else {
        throw new Error("Unexpected metrics data format");
      }

      if (onAnalysisComplete) {
        onAnalysisComplete(metrics);
      }
    } catch (error) {
      let errorMessage = "Failed to fetch metrics.";

      if (error instanceof Error) {
        if (error.message.includes("No metrics found")) {
          errorMessage = error.message;
        } else if (error.message.includes("Failed to fetch")) {
          errorMessage =
            "Network error: Unable to connect to the server. Please check your internet connection and try again.";
        } else {
          errorMessage = error.message;
        }
      }

      setMetricsError(errorMessage);
    } finally {
      setIsFetchingMetrics(false);
    }
  };

  return {
    // State
    analysisError,
    isFetchingMetrics,
    metricsError,
    showAnalysisConfirmation,
    showRequestSent,
    dataFetchedFromDatabase,

    // Actions
    handleStartAnalysis,
    handleFetchMetrics,
    testNetworkConnectivity,
    setShowAnalysisConfirmation,
    setDataFetchedFromDatabase,
    setAnalysisError,
    setMetricsError,
  };
};
